import {
  DropDownList,
  DropDownListFilterChangeEvent,
  ListItemProps,
} from '@progress/kendo-react-dropdowns';
import React, { KeyboardEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { Keys } from 'src/constants/keys';
import useBoolean from 'src/hooks/useBoolean';
import useInvoiceScanManager from 'src/hooks/useInvoiceScanManager';
import { IAllSuppliers } from 'src/services/useGetOnlineAndOfflineSuppliers';
import { removeSpecialCharacters } from 'src/utils/strings';
import { defaultSelectedSupplier } from '../InvoiceScanManagerContext';
import { GetColor } from './GetColor';
import { InputColorMap, TextColorMap } from './PoNumberInput';

interface SupplierGroup {
  title: string;
  suppliersList: IAllSuppliers[];
}

export default function SupplierInput() {
  const {
    selectedSupplier,
    setSelectedSupplier,
    reviewPoValidations,
    supplierName,
    reviewDocumentValidations,
    onlineOfflineSuppliers,
    initialLoading,
    isReviewPanelLoading,
    setInitialSelectedSupplier,
    initialReviewPoValidation,
  } = useInvoiceScanManager();

  const { value: open, setTrue: openDropdown, setFalse: closeDropdown } = useBoolean();
  const [filteredSuppliers, setFilteredSuppliers] = useState<SupplierGroup[]>([]);
  const [filterValue, setFilterValue] = useState<string>('');

  const possibleSupplier = useMemo(() => {
    const suppliersList: IAllSuppliers[] = [];
    const { supplierMatchReviewResult } = initialReviewPoValidation;

    if (supplierMatchReviewResult?.addressMatchedSupplier) {
      suppliersList.push(supplierMatchReviewResult?.addressMatchedSupplier);
    }
    if (supplierMatchReviewResult?.customerIdMatchedSuppliers.length > 0) {
      suppliersList.push(...supplierMatchReviewResult?.customerIdMatchedSuppliers);
    }
    if (reviewPoValidations?.poMatchedSuppliers) {
      suppliersList.push(...reviewPoValidations?.poMatchedSuppliers);
    }

    if (reviewDocumentValidations?.matchedSuppliers) {
      suppliersList.push(...reviewDocumentValidations?.matchedSuppliers);
    }
    const uniqueSuppliers: IAllSuppliers[] = suppliersList.reduce(
      (accumulator: IAllSuppliers[], current: IAllSuppliers) => {
        if (!accumulator.find((item) => item.supplierId === current.supplierId)) {
          accumulator.push(current);
        }
        return accumulator;
      },
      []
    );

    return uniqueSuppliers.sort((a, b) =>
      a.supplierName.toLowerCase().localeCompare(b.supplierName.toLowerCase())
    );
  }, [
    initialReviewPoValidation,
    reviewDocumentValidations?.matchedSuppliers,
    reviewPoValidations?.poMatchedSuppliers,
  ]);

  const possibleSharedSupplier = useMemo(
    () =>
      possibleSupplier.filter((ps) =>
        onlineOfflineSuppliers.some((os) => ps.supplierId === os.supplierId)
      ),
    [onlineOfflineSuppliers, possibleSupplier]
  );

  const allOnlineOfflineSuppliers = useMemo(
    () =>
      onlineOfflineSuppliers.filter(
        (s) => !possibleSharedSupplier.some((p) => p.supplierId === s.supplierId)
      ),
    [onlineOfflineSuppliers, possibleSharedSupplier]
  );

  const isPoConfirmed =
    reviewPoValidations.matchStatus === 'ConfirmedMatchCreatedByCurrentUser' ||
    reviewPoValidations.matchStatus === 'ConfirmedMatchCreatedByOtherUser';

  const ocrMatchSupplier = useMemo(
    () =>
      onlineOfflineSuppliers.find((s) =>
        removeSpecialCharacters(s.supplierName)
          .toLowerCase()
          .trim()
          .includes(removeSpecialCharacters(supplierName.value).toLowerCase().trim())
      ),
    [onlineOfflineSuppliers, supplierName.value]
  );

  const suppliers: SupplierGroup[] = useMemo(
    () => [
      {
        title: 'Possible Matches',
        suppliersList: possibleSharedSupplier,
      },
      {
        title: '',
        suppliersList: allOnlineOfflineSuppliers,
      },
    ],
    [allOnlineOfflineSuppliers, possibleSharedSupplier]
  );

  useEffect(() => {
    if (!initialLoading && !isReviewPanelLoading) {
      setFilterValue('');
      setFilteredSuppliers(suppliers);
      let supplier: IAllSuppliers = defaultSelectedSupplier;
      if (isPoConfirmed) {
        supplier = reviewPoValidations.poMatchedSuppliers[0];
      } else if (supplierName.value && ocrMatchSupplier) {
        supplier = ocrMatchSupplier;
      } else if (possibleSharedSupplier.length === 1) {
        supplier = possibleSharedSupplier[0];
      }
      setInitialSelectedSupplier(supplier);
      setSelectedSupplier(supplier);
    }
  }, [
    initialLoading,
    isPoConfirmed,
    isReviewPanelLoading,
    ocrMatchSupplier,
    possibleSharedSupplier,
    reviewPoValidations.poMatchedSuppliers,
    setInitialSelectedSupplier,
    setSelectedSupplier,
    supplierName.value,
    suppliers,
  ]);

  const handleSupplierChange = useCallback(
    (supplier: IAllSuppliers) => {
      setSelectedSupplier(supplier);
    },
    [setSelectedSupplier]
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLSpanElement>) => {
      const spans = document.querySelectorAll('.supplier-dropdown-span');
      if (spans.length === 0) return;

      const activeClass = CSS.escape('bg-[#086fff]');
      const currentHighlighted = document.querySelector(`.supplier-dropdown-span.${activeClass}`);
      let currentIndex = Array.from(spans).indexOf(currentHighlighted as HTMLElement);
      if (event.key === Keys.enter) {
        const supplierId = currentHighlighted?.getAttribute('data-supplier-id');
        if (!supplierId) return;

        const selectedSupplier = filteredSuppliers
          .flatMap((group) => group.suppliersList)
          .find((supplier) => supplier.supplierId.toString() === supplierId);

        if (selectedSupplier) {
          handleSupplierChange(selectedSupplier);
        }
        return;
      } else if (event.key === Keys.up) {
        if (currentIndex > 0) {
          currentIndex -= 1;
        } else {
          return;
        }
      } else if (event.key === Keys.down) {
        if (currentIndex < spans.length - 1) {
          currentIndex += 1;
        } else {
          return;
        }
      }

      spans.forEach((span) => {
        span.classList.remove('bg-[#086fff]', 'text-white');
      });

      const newHighlighted = spans[currentIndex] as HTMLElement;
      newHighlighted.classList.add('bg-[#086fff]', 'text-white');

      newHighlighted.scrollIntoView({ behavior: 'smooth', block: 'center' });
    },
    [filteredSuppliers, handleSupplierChange]
  );

  const itemRender = useCallback(
    (li: React.ReactElement<HTMLLIElement>, itemProps: ListItemProps) => {
      const { dataItem, index } = itemProps;
      const hasPossibleSuppliers = index === 0 && dataItem.suppliersList.length > 0;
      return (
        <li className="text-[13px]">
          <div className="flex flex-col">
            {hasPossibleSuppliers && (
              <label key={dataItem.title} className="text-[#777777] pl-4">
                {dataItem.title}
              </label>
            )}
            {dataItem.suppliersList.map((s: IAllSuppliers) => (
              <span
                key={s.supplierId}
                onClick={() => handleSupplierChange(s)}
                className={`pl-3 k-list-item font-semibold supplier-dropdown-span min-w-fit`}
                data-supplier-id={s.supplierId}
                onKeyDown={handleKeyDown}
              >
                {s.supplierName}
              </span>
            ))}
            {hasPossibleSuppliers && <span className="border-t-2 mx-4 pb-1" />}
          </div>
        </li>
      );
    },
    [handleSupplierChange, handleKeyDown]
  );

  useEffect(() => {
    if (open && filteredSuppliers.length > 0) {
      const spans = document.querySelectorAll('.supplier-dropdown-span');
      if (spans.length > 0) {
        spans.forEach((span) => span.classList.remove('bg-[#086fff]', 'text-white'));
        spans[0].classList.add('bg-[#086fff]', 'text-white');
        spans[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [open, filteredSuppliers]);

  const filterData = useCallback(
    (filter: string, data: IAllSuppliers[]) =>
      data.filter((s) => s.supplierName.toLowerCase().includes(filter.toLowerCase())),
    []
  );

  const filterChange = (event: DropDownListFilterChangeEvent) => {
    setFilterValue(event.filter.value);
    const filteredData = suppliers
      .map((group) => {
        const filteredSuppliersList = filterData(event.filter.value, group.suppliersList);

        return {
          ...group,
          suppliersList: filteredSuppliersList,
        };
      })
      .filter((s) => s.suppliersList.length > 0);
    setFilteredSuppliers(filteredData);
  };

  const { isSupplierFound, isConfirmSupplier, confirmSupplierType } = useMemo(() => {
    let isSupplierFound = false;
    let isConfirmSupplier = false;
    let confirmSupplierType: 'error' | 'success' | 'loading' = isReviewPanelLoading
      ? 'loading'
      : 'error';
    if (supplierName.value && ocrMatchSupplier) {
      isSupplierFound = true;
    }

    if (!isReviewPanelLoading && selectedSupplier.supplierName) {
      isConfirmSupplier = true;
      confirmSupplierType = 'success';
    }

    return { isSupplierFound, isConfirmSupplier, confirmSupplierType };
  }, [isReviewPanelLoading, ocrMatchSupplier, selectedSupplier.supplierName, supplierName.value]);

  const dropdownValue = useMemo(
    () => (!isReviewPanelLoading ? selectedSupplier?.supplierName || 'Select one' : ''),
    [isReviewPanelLoading, selectedSupplier?.supplierName]
  );

  const confirmSupplierColor = GetColor(confirmSupplierType);

  return (
    <div>
      <p className="text-sm font-medium mb-1">Supplier</p>
      {!initialLoading && isPoConfirmed ? (
        <div>
          <div
            className={`k-input k-input-sm k-input-solid k-rounded-md !bg-[#AAAAAA33] !border-2 border-[#C6C6C6] ${
              InputColorMap[GetColor('success')]
            } h-[32px]`}
            data-tooltip-id={'global-tooltip'}
            data-tooltip-content={selectedSupplier.supplierName}
          >
            <span className="k-input-inner !text-black">{selectedSupplier.supplierName}</span>
          </div>
          <span className={`text-sm ${TextColorMap[GetColor('success')]}`}>
            Supplier pulled from PO.
          </span>
        </div>
      ) : (
        <div className="flex flex-row gap-2">
          <div className={`w-[50%] flex flex-col gap-1`}>
            <div className="flex justify-between">
              <p className="w-full text-sm">OCR Result</p>
              <p className="text-sm text-black font-medium">
                {!initialLoading && supplierName.matchConfidence
                  ? `*${supplierName.matchConfidence}`
                  : ''}
              </p>
            </div>
            <div
              className={`k-input k-input-sm k-input-solid k-rounded-md !bg-[#AAAAAA33] border border-[#C6C6C6]  h-[30px]`}
              data-tooltip-id={initialLoading ? '' : 'global-tooltip'}
              data-tooltip-content={supplierName.value}
            >
              <span className="px-2 py-1 !text-black !truncate">
                {!initialLoading && supplierName.value}
              </span>
            </div>
            {!initialLoading && !isSupplierFound && (
              <span className="text-sm">Supplier not found.</span>
            )}
          </div>

          <div className="flex flex-col w-[50%] gap-1">
            <span className="w-full text-sm">Confirm Supplier</span>
            <div
              data-tooltip-id={'global-tooltip'}
              data-tooltip-content={dropdownValue}
              className="supplier-dropdown"
              onKeyDown={handleKeyDown}
            >
              <DropDownList
                className={`!mb-0 ml-2 m-2 !bg-white text-sm ${InputColorMap[confirmSupplierColor]}`}
                data={filteredSuppliers}
                value={dropdownValue}
                dataItemKey="title"
                filterable
                onClose={closeDropdown}
                onOpen={openDropdown}
                onFilterChange={filterChange}
                size={'small'}
                itemRender={itemRender}
                loading={!initialLoading && isReviewPanelLoading}
                filter={filterValue}
                disabled={isReviewPanelLoading}
              />
            </div>
            {!isReviewPanelLoading && !isConfirmSupplier && (
              <span className={`text-sm ${TextColorMap[confirmSupplierColor]}`}>
                Confirm Supplier.
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
